/**
 * OKS-512 — Canonical blazon glyph + tier-palette source of truth for
 * `<RankPatch>`.
 *
 * # Glyph provenance (the lucide-static rule)
 *
 * The four glyph path sets below are transcribed VERBATIM from
 * `lucide-static@0.577.0` (matches the repo's `lucide-react@^0.577.0`),
 * fetched from `unpkg.com/lucide-static@0.577.0/icons/<name>.svg`. They
 * are NOT hand-crafted — hand-drawing path data produces stroke
 * artifacts at the blazon's small render sizes. We inline the raw `d`
 * strings (rather than depending on the `lucide-static` npm package or
 * nesting a `lucide-react` <svg>) so the whole blazon lives in ONE
 * `viewBox` and scales uniformly from `lg` down to avatar-corner size
 * with zero overlay subpixel drift.
 *
 * To refresh: re-fetch the four SVGs at the pinned lucide version and
 * copy the inner `<path d="...">` values. Source viewBox is `0 0 24 24`,
 * stroke-based (fill=none, white stroke in the blazon).
 *
 * # Blazon mapping (UX Spec §1)
 *
 * Keyed on 1-based ladder POSITION, never name (PRD/UX Open Q1) — keeps
 * the patch correct when brands rename tiers (OKS-52). For the only
 * real case today (`tierCount === 4`) we pin the EXACT Figma palette;
 * other counts fall to a normalization algorithm (floor→sprout/bronze,
 * top→crown/gold, middle interpolates orange→silver) so a future 3- or
 * 6-tier ladder still reads sensibly and never crashes.
 */

export type GlyphName = "sprout" | "chevrons-up" | "star" | "crown"

/**
 * Inner `<path d>` sets per glyph — canonical lucide-static@0.577.0.
 * Source viewBox `0 0 24 24`; rendered with `fill=none` + white stroke.
 */
export const GLYPH_PATHS: Record<GlyphName, readonly string[]> = {
  // lucide-static@0.577.0 — icon=sprout
  sprout: [
    "M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4 4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3",
    "M4 9a5 5 0 0 1 8 4 5 5 0 0 1-8-4",
    "M5 21h14",
  ],
  // lucide-static@0.577.0 — icon=chevrons-up
  "chevrons-up": ["m17 11-5-5-5 5", "m17 18-5-5-5 5"],
  // lucide-static@0.577.0 — icon=star
  star: [
    "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z",
  ],
  // lucide-static@0.577.0 — icon=crown
  crown: [
    "M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z",
    "M5 21h14",
  ],
}

/** A resolved blazon: which glyph, the shield gradient, and pip count. */
export interface Blazon {
  glyph: GlyphName
  /** Top stop of the vertical shield gradient (hex). */
  gradientFrom: string
  /** Bottom (darker) stop of the shield gradient (hex). */
  gradientTo: string
  /** Filled star pips — logical count (1-based position, capped at 5). */
  pips: number
}

// Tier palettes (UX Spec §1). Silver uses a darkened slate so the white
// glyph/pips keep AA contrast (UX Spec §5).
const BRONZE = { from: "#8a5a2b", to: "#5e3d1c" }
const ORANGE = { from: "#f0922f", to: "#d96f15" }
const SILVER = { from: "#aab4c4", to: "#7f8a9c" }
const GOLD = { from: "#f7c23c", to: "#e09e12" }

/** Pips are visually capped — the glyph carries identity past this. */
export const MAX_PIPS = 5

/**
 * The exact, Figma-pinned 4-tier ladder (today's only real case). Kept
 * discrete (not interpolated) so production stays pixel-accurate.
 */
export const TIER4_BLAZONS: Record<1 | 2 | 3 | 4, Blazon> = {
  1: {
    glyph: "sprout",
    gradientFrom: BRONZE.from,
    gradientTo: BRONZE.to,
    pips: 1,
  },
  2: {
    glyph: "chevrons-up",
    gradientFrom: ORANGE.from,
    gradientTo: ORANGE.to,
    pips: 2,
  },
  3: {
    glyph: "star",
    gradientFrom: SILVER.from,
    gradientTo: SILVER.to,
    pips: 3,
  },
  4: { glyph: "crown", gradientFrom: GOLD.from, gradientTo: GOLD.to, pips: 4 },
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

/** Linear interpolation between two `#rrggbb` hex colors. */
function lerpHex(a: string, b: string, t: number): string {
  const channels = [1, 3, 5].map((i) => {
    const from = parseInt(a.slice(i, i + 2), 16)
    const to = parseInt(b.slice(i, i + 2), 16)
    return Math.round(from + (to - from) * t)
  })
  return `#${channels.map((c) => c.toString(16).padStart(2, "0")).join("")}`
}

/**
 * Resolve the blazon for a ladder position.
 *
 * @param tier 1-based position (clamped into `1..tierCount`).
 * @param tierCount total tiers in the brand's ladder (coerced `>= 1`).
 *
 * `tierCount === 4` → exact Figma palette. Otherwise normalizes:
 * floor → sprout/bronze, top → crown/gold, middle → chevrons-up (lower
 * half) / star (upper half) with an orange→silver gradient lerp. Pips =
 * `min(position, 5)`. Never throws on count `1`, `7`, or out-of-range
 * `tier`.
 */
export function blazonFor(tier: number, tierCount: number): Blazon {
  const count = Math.max(1, Math.floor(tierCount || 1))
  const position = clamp(Math.round(tier || 1), 1, count)

  if (count === 4) {
    return TIER4_BLAZONS[position as 1 | 2 | 3 | 4]
  }

  const band = count > 1 ? (position - 1) / (count - 1) : 0
  const pips = Math.min(position, MAX_PIPS)

  if (position === 1) {
    return {
      glyph: "sprout",
      gradientFrom: BRONZE.from,
      gradientTo: BRONZE.to,
      pips,
    }
  }
  if (position === count) {
    return {
      glyph: "crown",
      gradientFrom: GOLD.from,
      gradientTo: GOLD.to,
      pips,
    }
  }

  return {
    glyph: band < 0.5 ? "chevrons-up" : "star",
    gradientFrom: lerpHex(ORANGE.from, SILVER.from, band),
    gradientTo: lerpHex(ORANGE.to, SILVER.to, band),
    pips,
  }
}

/**
 * OKS-539 — Position-derived default tint for a rank blazon (frame-less
 * image-with-default). Reuses the SAME shield tier palette as
 * `<RankPatch>` (`blazonFor`) so an un-uploaded rank's plain-color
 * placeholder stays visually consistent with the rest of the identity
 * system (PRD Q1). Returns a top-to-bottom CSS `linear-gradient` string
 * (the shield's vertical gradient) for the `<BadgeImage defaultStyle>`
 * prop. Interim per CTPO — real art swaps in later.
 *
 * @param tier 1-based ladder position.
 * @param tierCount total tiers in the brand's ladder.
 */
export function rankTintGradient(tier: number, tierCount: number): string {
  const blazon = blazonFor(tier, tierCount)
  return `linear-gradient(to bottom, ${blazon.gradientFrom}, ${blazon.gradientTo})`
}

/**
 * Build an SVG polygon `points` string for a 5-pointed pip star centered
 * at `(cx, cy)`. Pure geometry — keeps pips crisp at every blazon size
 * without extra path-data transcription.
 */
export function pipStarPoints(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  points = 5,
): string {
  const coords: string[] = []
  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outer : inner
    const angle = -Math.PI / 2 + (i * Math.PI) / points
    const x = cx + radius * Math.cos(angle)
    const y = cy + radius * Math.sin(angle)
    coords.push(`${x.toFixed(2)},${y.toFixed(2)}`)
  }
  return coords.join(" ")
}
